import { Link } from "@tanstack/react-router";
import { ArrowRight, Landmark, Sparkles } from "lucide-react";
import { motion, type Variants } from "motion/react";

import { AnimatedGridPattern } from "@/components/magicui/animated-grid-pattern";
import { m } from "@/paraglide/messages.js";

const EASE = [0.25, 0.1, 0.25, 1] as const;

const backdropVariants: Variants = {
  hidden: { opacity: 0, scale: 1.08 },
  show: { opacity: 1, scale: 1, transition: { duration: 1.4, ease: EASE } },
};

const contentVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.18, delayChildren: 0.2 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const ctaVariants: Variants = {
  hidden: { opacity: 0, y: 18, scale: 0.94 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: EASE, delay: 0.75 } },
};

const scrollToCapabilities = () => {
  document.getElementById("capabilities")?.scrollIntoView({ behavior: "smooth" });
};

/**
 * Landing hero. A rounded panel with a token-driven backdrop: blurred primary
 * gradient blobs washed by the shared animated grid pattern. Everything reads
 * from the `--primary` / `--secondary` / `--foreground` tokens, so retheming
 * the app restyles the hero with no edits here. Elements stagger in on mount.
 */
export const HeroSection = () => {
  return (
    <section data-tour="landing-hero" className="relative mx-auto mt-6 w-full px-4 md:px-0">
      <div className="relative mx-auto flex w-full max-w-[1260px] flex-col items-center overflow-hidden rounded-2xl border border-border/60 pb-10 md:h-[680px] lg:h-[720px]">
        <motion.div
          aria-hidden
          className="absolute inset-0 z-0"
          variants={backdropVariants}
          initial="hidden"
          animate="show"
        >
          <HeroBackdrop />
        </motion.div>

        <motion.div
          className="relative z-10 mt-16 flex w-full max-w-[820px] flex-col items-center gap-5 px-4 text-center md:mt-24 lg:mt-28"
          variants={contentVariants}
          initial="hidden"
          animate="show"
        >
          <div
            className="absolute -top-10 -translate-y-1/2 left-1/2 scale-150 -translate-x-1/2 blur-2xl opacity-50 pointer-events-none -z-10"
            aria-hidden="true"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="434" height="427">
              <defs>
                <linearGradient id="flare" x1="0%" x2="100%" y1="0%" y2="100%">
                  <stop offset="0%" stopColor="var(--primary)" />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                fill="url(#flare)"
                fillRule="evenodd"
                d="m410 0 461 369-284 58z"
                transform="matrix(1 0 0 1 -410 0)"
              ></path>
            </svg>
          </div>
          <motion.span variants={itemVariants}>
            <div className="relative inline-flex before:absolute before:inset-0 before:rounded-full dark:before:bg-primary dark:before:blur-md">
              <span className="relative inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-primary/20 bg-background/60 px-3 py-0.5 text-sm font-medium text-primary shadow-sm dark:border-transparent dark:bg-transparent dark:text-primary-foreground dark:[background:linear-gradient(var(--primary),var(--primary))_padding-box,linear-gradient(var(--primary),color-mix(in_oklch,var(--primary),white_70%)_75%,transparent_100%)_border-box] before:pointer-events-none before:absolute before:inset-0 before:rounded-full dark:before:bg-muted/50">
                <span className="relative inline-flex items-center text-primary dark:text-foreground">
                  <Landmark className="size-4 text-primary mr-2" />
                  {m.landing_hero_badge()}
                </span>
              </span>
            </div>
          </motion.span>
          <motion.h1
            variants={itemVariants}
            className="text-balance text-4xl font-semibold leading-[1.08] tracking-tight text-foreground md:text-6xl lg:text-[68px]"
          >
            <AccentedPeriods text={m.landing_hero_title()} />
          </motion.h1>
          <motion.p
            variants={itemVariants}
            className="max-w-xl text-base font-medium leading-relaxed text-muted-foreground md:text-lg"
          >
            {m.landing_hero_subtitle()}
          </motion.p>
        </motion.div>

        <motion.div
          className="relative z-10 mt-7 flex flex-col items-center gap-4 sm:inline-flex sm:flex-row sm:justify-center"
          variants={ctaVariants}
          initial="hidden"
          animate="show"
        >
          <Link
            to="/contact"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-linear-to-r from-foreground/80 via-foreground to-foreground/80 px-8 py-3 text-base font-medium text-background shadow-lg transition-colors duration-150 ease-in-out hover:bg-foreground sm:w-auto"
          >
            {m.landing_cta_request_demo()}
            <span className="text-primary transition-transform duration-150 ease-in-out group-hover:translate-x-0.5">
              <ArrowRight className="size-4 text-background" />
            </span>
          </Link>
          <span className="inline-flex rounded-full bg-background">
            <button
              type="button"
              onClick={scrollToCapabilities}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-8 py-3 text-base font-medium text-foreground shadow-lg backdrop-blur-sm transition-colors duration-150 ease-in-out hover:bg-primary/15 sm:w-auto"
            >
              <Sparkles className="size-4" />
              {m.landing_hero_see_capabilities()}
            </button>
          </span>
        </motion.div>
      </div>
    </section>
  );
};

/** Renders a headline with every sentence-ending period in the brand pink. */
function AccentedPeriods({ text }: { text: string }) {
  return (
    <>
      {text
        .split(/(\.)/)
        .filter(Boolean)
        .map((part, index) =>
          part === "." ? (
            <span key={`${index}-dot`} className="text-pink-500">
              .
            </span>
          ) : (
            <span key={`${index}-text`}>{part}</span>
          ),
        )}
    </>
  );
}

function HeroBackdrop() {
  return (
    <>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 1220 810"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid slice"
        className="h-full w-full"
      >
        <defs>
          <linearGradient id="hero-blob-1" x1="1118" y1="-149" x2="1118" y2="1249" gradientUnits="userSpaceOnUse">
            <stop stopColor="var(--foreground)" />
            <stop offset="0.578" stopColor="color-mix(in oklch, var(--primary), var(--muted) 45%)" />
            <stop offset="1" stopColor="var(--primary)" />
          </linearGradient>
          <linearGradient id="hero-blob-2" x1="1054" y1="-213" x2="1054" y2="1185" gradientUnits="userSpaceOnUse">
            <stop stopColor="var(--foreground)" />
            <stop offset="0.578" stopColor="color-mix(in oklch, var(--primary), var(--muted) 45%)" />
            <stop offset="1" stopColor="var(--primary)" />
          </linearGradient>
          <linearGradient id="hero-blob-3" x1="1238" y1="-293" x2="1238" y2="1105" gradientUnits="userSpaceOnUse">
            <stop stopColor="var(--foreground)" />
            <stop offset="0.578" stopColor="color-mix(in oklch, var(--secondary), var(--muted) 45%)" />
            <stop offset="1" stopColor="var(--secondary)" />
          </linearGradient>
          <radialGradient
            id="hero-blob-4"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(989 557) rotate(48) scale(466 471)"
          >
            <stop stopColor="var(--foreground)" />
            <stop offset="0.158" stopColor="color-mix(in oklch, var(--primary), var(--muted) 45%)" />
            <stop offset="1" stopColor="var(--primary)" />
          </radialGradient>
          <filter id="hero-blur" x="-20%" y="-20%" width="140%" height="140%" colorInterpolationFilters="sRGB">
            <feGaussianBlur stdDeviation="120" />
          </filter>
        </defs>

        <g filter="url(#hero-blur)">
          <path
            d="M1447 -87v-62H1770V1249H466V894C1008 894 1447 455 1447 -87Z"
            fill="url(#hero-blob-1)"
            opacity="0.55"
          />
          <path
            d="M1383 -151v-62H1706V1185H402V830C944 830 1383 391 1383 -151Z"
            fill="url(#hero-blob-2)"
            opacity="0.4"
          />
          <path
            d="M1567 -231v-62H1890V1105H586V750C1128 750 1567 311 1567 -231Z"
            fill="url(#hero-blob-3)"
            opacity="0.35"
          />
          <path
            d="M66 750h218c576 0 1043-467 1043-1043h323v1398H66V750Z"
            fill="url(#hero-blob-4)"
            opacity="0.3"
          />
        </g>
      </svg>

      <AnimatedGridPattern
        width={46}
        height={46}
        numSquares={10}
        maxOpacity={0.04}
        strokeDasharray={2}
        duration={5}
        repeatDelay={4.5}
        className="text-muted-foreground/40 stroke-foreground/8 [mask-image:radial-gradient(ellipse_at_center,black_25%,transparent_72%)]"
      />
    </>
  );
}
